// U1b-FE — the single user-management API module. Consolidates the user hooks
// that were previously forked between this file (legacy/broken) and api/s4.ts
// (correct). All point at the U1b-BE endpoints, which are effective-tenant
// correct (the ?tenant_id interceptor re-scopes a super-admin's switch) and
// gated on manage_users.
//
// Roles live in the roles domain (api/s4.ts) but every user-management role
// dropdown sources from here via the re-export below, so there is one import
// surface for the Users/Roles pages.
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { useTenantStore } from "@/store/tenant";
import type { Role, UserDetail, UserList } from "@/types";

export { useRoles } from "./s4";
export type { Role as RoleRow } from "./s4";

const usersKey = ["users"] as const;

// List — keyed on the active tenant so a super-admin's tenant switch re-scopes
// the list without a page refresh (the request interceptor appends ?tenant_id;
// keying the query separates the cache and forces a refetch on switch).
export function useUsers() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  return useQuery({
    queryKey: [...usersKey, "list", tenantId],
    queryFn: async () => {
      const { data } = await api.get<UserList>("/api/v1/users?limit=200");
      return data;
    },
  });
}

// Token invite — the SOLE invite path (legacy POST /users/invite was removed in
// U1b-BE). legacy_role (U1a-3b) sets the invitee's base visibility at accept
// time; role_id grants tenant-role permissions. The invitee sets their OWN
// password via the accept link — no password travels through this form.
export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      role_id: string;
      legacy_role: Role;
      entity_id?: string | null;
      scope?: "subtree" | "exact";
    }) => {
      const { data } = await api.post<{
        id: string;
        email: string;
        expires_at: string;
        accept_url_hint: string;
      }>("/api/v1/users/invite-with-token", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

// Profile / legacy-role / site-scope edit → PATCH /users/{id}.
export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: {
      role?: Role;
      is_active?: boolean;
      site_ids?: string[];
      full_name?: string;
      must_change_password?: boolean;
    }) => {
      const { data } = await api.patch<UserDetail>(`/api/v1/users/${id}`, patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

// "Deactivate" = suspend (session-revoking). Replaces the legacy DELETE
// /users/{id} (which only flipped is_active and left sessions live).
export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<{
        id: string;
        is_active: boolean;
        revoked_sessions: number;
      }>(`/api/v1/users/${userId}/suspend`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<{ id: string; is_active: boolean }>(
        `/api/v1/users/${userId}/reactivate`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

// ---------------------------------------------------------------------------
// Role-table grants (per-entity), used on the user detail page.
// ---------------------------------------------------------------------------
export interface UserEntityRoleRow {
  id: string;
  user_id: string;
  role_id: string;
  entity_id: string;
  scope: "subtree" | "exact";
}

export function useUserRoles(userId: string | undefined) {
  return useQuery({
    queryKey: ["users", userId, "roles"],
    queryFn: async () => {
      const { data } = await api.get<UserEntityRoleRow[]>(
        `/api/v1/users/${userId}/roles`,
      );
      return data;
    },
    enabled: Boolean(userId),
  });
}

export function useAssignUserRole(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      role_id: string;
      entity_id: string;
      scope: "subtree" | "exact";
    }) => {
      const { data } = await api.post<UserEntityRoleRow>(
        `/api/v1/users/${userId}/roles`,
        input,
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["users", userId, "roles"] }),
  });
}

export function useRevokeUserRole(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      await api.delete(`/api/v1/users/${userId}/roles/${assignmentId}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["users", userId, "roles"] }),
  });
}
